import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { sepayAPI } from "../services/api";
import { Landmark, RefreshCw, CheckCircle2, AlertTriangle, HelpCircle, Undo2, ArrowUpCircle, Copy, Ban } from "lucide-react";

type MatchStatus = "matched" | "underpaid" | "overpaid" | "late" | "duplicate" | "unmatched";

interface SepayTx {
  _id: string;
  sepayId: string;
  gateway: string;
  amount: number;
  content: string;
  transactionDate: string;
  matchStatus: MatchStatus;
  expectedAmount: number | null;
  paidTotal: number | null;
  resolved: boolean;
  resolvedNote: string;
  resolvedAt: string | null;
  resolvedBy: { name?: string; email?: string } | null;
  bookingId: {
    _id: string;
    guestName?: string;
    guestPhone?: string;
    roomName?: string;
    finalPrice?: number;
    paidAmount?: number;
    status?: string;
    checkInDate?: string;
  } | null;
  createdAt: string;
}

const STATUS_META: Record<MatchStatus, { label: string; cls: string; icon: React.ReactNode }> = {
  matched:   { label: "Tự xác nhận",  cls: "bg-green-50 text-green-700 border-green-200", icon: <CheckCircle2 size={14} /> },
  underpaid: { label: "Chuyển thiếu", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <AlertTriangle size={14} /> },
  overpaid:  { label: "Chuyển dư",    cls: "bg-sky-50 text-sky-700 border-sky-200",       icon: <ArrowUpCircle size={14} /> },
  late:      { label: "Đơn đã huỷ",   cls: "bg-orange-50 text-orange-700 border-orange-200", icon: <Ban size={14} /> },
  duplicate: { label: "Chuyển trùng", cls: "bg-purple-50 text-purple-700 border-purple-200", icon: <Copy size={14} /> },
  unmatched: { label: "Không khớp đơn", cls: "bg-red-50 text-red-700 border-red-200",     icon: <HelpCircle size={14} /> },
};

function formatCurrency(n: number | null | undefined): string {
  return new Intl.NumberFormat("vi-VN").format(n || 0) + "đ";
}

export default function SepayTransactionsPage(): React.ReactElement {
  const [items, setItems] = useState<SepayTx[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [view, setView] = useState<"pending" | "all">("pending");
  const [status, setStatus] = useState<"" | MatchStatus>("");
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const limit = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sepayAPI.getTransactions({
        page,
        limit,
        status: status || undefined,
        resolved: view === "pending" ? false : undefined,
      });
      setItems(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
      setPendingCount(res.data.pendingCount || 0);
    } catch {
      toast.error("Lỗi khi tải giao dịch");
    } finally {
      setLoading(false);
    }
  }, [page, status, view]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resolve = async (tx: SepayTx, resolved: boolean) => {
    let note = "";
    if (resolved) {
      const input = window.prompt("Ghi chú xử lý (vd: đã gọi khách, đã xác nhận đơn #ABC123, đã hoàn tiền…):", "");
      if (input === null) return;
      note = input;
    }
    try {
      await sepayAPI.resolve(tx._id, resolved, note);
      toast.success(resolved ? "Đã đánh dấu xử lý" : "Đã mở lại giao dịch");
      fetchData();
    } catch {
      toast.error("Không cập nhật được giao dịch");
    }
  };

  const confirmBooking = async (tx: SepayTx) => {
    const b = tx.bookingId;
    if (!b) return;
    const code = b._id.slice(-6).toUpperCase();
    const paid = tx.paidTotal ?? tx.amount;
    const missing = (b.finalPrice ?? 0) - paid;
    const warn = missing > 0 ? `\n\nLưu ý: khách mới chuyển ${formatCurrency(paid)}, còn thiếu ${formatCurrency(missing)}.` : "";
    if (!window.confirm(`Xác nhận đơn #${code} (${b.guestName || "khách"} · ${b.roomName || ""})? Khách sẽ nhận mail xác nhận.${warn}`)) return;
    try {
      await sepayAPI.confirmBooking(tx._id);
      toast.success(`Đã xác nhận đơn #${code}`);
      fetchData();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast.error(msg || "Không xác nhận được đơn");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <Landmark size={22} className="text-coffee-600" />
            Giao dịch chuyển khoản
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Tiền vào tài khoản do SePay báo về — xử lý tay các giao dịch chuyển thiếu hoặc không khớp đơn
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-lg text-sm text-zinc-600 hover:bg-zinc-50 transition-colors"
        >
          <RefreshCw size={14} />
          Tải lại
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="inline-flex rounded-lg border border-zinc-200 bg-white p-0.5">
          {(["pending", "all"] as const).map((v) => (
            <button
              key={v}
              onClick={() => { setView(v); setPage(1); }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === v ? "bg-coffee-600 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
            >
              {v === "pending" ? `Cần xử lý${pendingCount ? ` (${pendingCount})` : ""}` : "Tất cả"}
            </button>
          ))}
        </div>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value as "" | MatchStatus); setPage(1); }}
          className="px-3 py-1.5 border border-zinc-200 rounded-lg text-sm bg-white text-zinc-700 focus:outline-none focus:border-coffee-400"
        >
          <option value="">Mọi trạng thái</option>
          <option value="matched">Tự xác nhận</option>
          <option value="underpaid">Chuyển thiếu</option>
          <option value="overpaid">Chuyển dư</option>
          <option value="late">Đơn đã huỷ</option>
          <option value="duplicate">Chuyển trùng</option>
          <option value="unmatched">Không khớp đơn</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">Đang tải...</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
          <Landmark size={40} className="mb-3 opacity-50" />
          {view === "pending" ? "Không có giao dịch nào cần xử lý 🎉" : "Chưa có giao dịch nào"}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Thời gian</th>
                <th className="text-right px-4 py-3 font-semibold text-zinc-600">Số tiền</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Nội dung CK</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Trạng thái</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Đơn</th>
                <th className="text-right px-4 py-3 font-semibold text-zinc-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.map((tx) => {
                const meta = STATUS_META[tx.matchStatus];
                const b = tx.bookingId;
                return (
                  <tr key={tx._id} className="hover:bg-zinc-50 align-top">
                    <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">
                      {tx.transactionDate || new Date(tx.createdAt).toLocaleString("vi-VN")}
                      <div className="text-xs text-zinc-400">#{tx.sepayId}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-zinc-900 whitespace-nowrap">
                      {formatCurrency(tx.amount)}
                      {tx.matchStatus === "underpaid" && tx.expectedAmount != null && (
                        <div className="text-xs font-normal text-amber-700">
                          đã nhận {formatCurrency(tx.paidTotal ?? tx.amount)} · còn thiếu {formatCurrency(tx.expectedAmount - (tx.paidTotal ?? tx.amount))}
                        </div>
                      )}
                      {tx.matchStatus === "overpaid" && tx.expectedAmount != null && tx.paidTotal != null && (
                        <div className="text-xs font-normal text-sky-700">dư {formatCurrency(tx.paidTotal - tx.expectedAmount)} — cần hoàn</div>
                      )}
                      {tx.matchStatus === "duplicate" && (
                        <div className="text-xs font-normal text-purple-700">đơn đã thanh toán — cần hoàn {formatCurrency(tx.amount)}</div>
                      )}
                      {tx.matchStatus === "late" && (
                        <div className="text-xs font-normal text-orange-700">đơn đã huỷ — hoàn tiền hoặc mở lại đơn</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-700 max-w-[260px] break-words">{tx.content}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${meta.cls}`}>
                        {meta.icon}
                        {meta.label}
                      </span>
                      {tx.resolved && tx.matchStatus !== "matched" && (
                        <div className="text-xs text-zinc-500 mt-1">
                          ✓ Đã xử lý{tx.resolvedBy?.name ? ` bởi ${tx.resolvedBy.name}` : ""}
                          {tx.resolvedNote && <div className="italic">“{tx.resolvedNote}”</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">
                      {b ? (
                        <>
                          <div className="font-mono font-semibold text-coffee-700">#{b._id.slice(-6).toUpperCase()}</div>
                          <div className="text-xs text-zinc-500">
                            {b.guestName} · {b.roomName}
                            <br />
                            {formatCurrency(b.finalPrice)} · {b.status}
                          </div>
                        </>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {!tx.resolved && b && b.status === "pending" && (
                        <button
                          onClick={() => confirmBooking(tx)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 mr-1.5 text-xs rounded-lg border border-green-300 text-green-700 hover:bg-green-50"
                        >
                          <CheckCircle2 size={12} /> Xác nhận đơn
                        </button>
                      )}
                      {tx.matchStatus !== "matched" && (
                        tx.resolved ? (
                          <button
                            onClick={() => resolve(tx, false)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-zinc-200 rounded-lg text-zinc-600 hover:bg-zinc-50"
                          >
                            <Undo2 size={12} /> Mở lại
                          </button>
                        ) : (
                          <button
                            onClick={() => resolve(tx, true)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-coffee-600 text-white hover:bg-coffee-700"
                          >
                            <CheckCircle2 size={12} /> Đã xử lý
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="px-3 py-1.5 border border-zinc-200 rounded-lg disabled:opacity-40 hover:bg-zinc-50">‹</button>
          <span className="text-zinc-600">Trang {page}/{totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}
            className="px-3 py-1.5 border border-zinc-200 rounded-lg disabled:opacity-40 hover:bg-zinc-50">›</button>
        </div>
      )}
    </div>
  );
}
